import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { MatSnackBar } from '@angular/material';
import { NgxSpinnerService } from 'ngx-spinner';
import { LocalSaveService } from './services/local-save-service.service';
import { environment } from 'src/environments/environment';
import { API_ENDPOINTS } from './config/api-endpoints.config';

/**
 * GatewayService - Facade
 * Maintains the same public interface consumed by 132+ components.
 * Endpoint definitions are now centralized in API_ENDPOINTS config.
 * Domain-specific services are available in /services/api/ for new code.
 */
@Injectable({
	providedIn: 'root'
})
export class GatewayService {

	globalip: string = environment.baseUrlPGV;
	globalipClientIsoft: string = environment.baseUrlClientIsoft;

	oktaLoginUrl: string = `${this.globalip}/newlogin`;
	globalipSM: string = environment.baseUrlSM;
	globalipSMC: string = environment.baseUrlSMC;
	whatsappSenderUrl: string = `${environment.whatsappSenderBaseUrl}/${environment.whatsappSenderId}`;
	token: any;

	constructor(
		public http: HttpClient,
		private localSave: LocalSaveService,
		private snackbar: MatSnackBar,
		private spinner: NgxSpinnerService) {
	}

	get(ruta, object) {
		return new Promise((resolve, reject) => {
			this.http.post<any>(
				this.globalip + API_ENDPOINTS.get(ruta),
				object,
				{ headers: { 'Authorization': 'p ' + this.localSave.get('OSR_TKN') } }
			).subscribe(
				result => {
					const auxresult: any = result;
					resolve(auxresult);
				},
				error => {
					reject(error);
				}
			);
		});
	}

	save(ruta, object) {
		try {
			return new Promise((resolve, reject) => {
				this.http.post<any>(
					this.globalip + API_ENDPOINTS.get(ruta),
					object,
					{ headers: { 'Authorization': 'p ' + this.localSave.get('OSR_TKN') } }
				).subscribe(
					result => {
						const auxresult: any = result;
						resolve(auxresult);
					},
					error => {
						if (error.status === 409) {
							this.spinner.hide();
							this.snackbar.open(error.error.msg, 'Cerrar', {
								duration: 3000
							});
							reject(false);
						} else {
							const auxerr: any = error;
							const rutaErr = auxerr.error.target.__zone_symbol__xhrURL;
							const rutaaux = rutaErr.split('/');
							if (rutaaux[rutaaux.length - 1] === 'ASIGNAR') {
								resolve('ASIGNAR');
							} else {
								this.spinner.hide();
								this.snackbar.open('Ha ocurrido un error, intenta de nuevo.', 'Cerrar', {
									duration: 3000
								});
								reject(false);
							}
						}
					}
				);
			});
		} catch (e) {
			throw e;
		}
	}

	update(ruta, object) {
		try {
			return new Promise((resolve, reject) => {
				this.http.put<any>(
					this.globalip + API_ENDPOINTS.get(ruta),
					object,
					{ headers: { 'Authorization': 'p ' + this.localSave.get('OSR_TKN') } }
				).subscribe(
					result => {
						resolve(result);
					},
					error => {
						if (error.status === 400) {
							this.snackbar.open(error.error.error, 'Cerrar', { duration: 3000 });
						} else {
							this.snackbar.open('Error inesperado', 'Cerrar', { duration: 3000 });
						}
						reject(error);
					}
				);
			});
		} catch (e) {
			throw e;
		}
	}

	delete(ruta, object, mensaje) {
		return new Promise((resolve, reject) => {
			this.http.post<any>(
				this.globalip + API_ENDPOINTS.get(ruta),
				object,
				{ headers: { 'Authorization': 'p ' + this.localSave.get('OSR_TKN') } }
			).subscribe(
				result => {
					this.snackbar.open(mensaje, '', {
						duration: 3000
					});
					resolve(1);
				},
				error => {
					if (error.status === 409) {
						this.snackbar.open(error.error.msg, 'Cerrar', {
							duration: 5000
						});
						resolve(1);
					} else {
						reject(1);
					}
				}
			);
		});
	}

	deleteAgente(ruta, object, mensaje, comentario) {
		return new Promise((resolve, reject) => {
			this.http.post<any>(
				this.globalip + API_ENDPOINTS.get(ruta),
				{ agente: object, coment: comentario },
				{ headers: { 'Authorization': 'p ' + this.localSave.get('OSR_TKN') } }
			).subscribe(
				result => {
					this.snackbar.open(mensaje, '', {
						duration: 3000
					});
					resolve(1);
				},
				error => {
					if (error.status === 409) {
						this.snackbar.open(error.error.msg, 'Cerrar', {
							duration: 5000
						});
						resolve(1);
					} else {
						reject(1);
					}
				}
			);
		});
	}

	// WS SM (Social Manager) - Observable-based methods
	gentokenSM() {
		return this.token = this.localSave.get('SMC');
	}

	loginSM(params: any) {
		return this.http.post(this.globalipSM + '/api/auth', params);
	}

	getbyid(params: any) {
		return this.http.post(this.globalipSM + '/api/socma/caso/getbyid', params, { headers: { 'Authorization': 'p ' + this.gentokenSM() } });
	}

	getchat(params: any) {
		return this.http.post(this.globalipSM + '/api/socma/chats', params, { headers: { 'Authorization': 'p ' + this.gentokenSM() } });
	}

	getfichacliente(params: any) {
		return this.http.post(this.globalipSM + '/api/socma/cliente/get', params, { headers: { 'Authorization': 'p ' + this.gentokenSM() } });
	}

	enviarMsj(params: any) {
		return this.http.post(this.globalipSM + '/api/socma/chats/sendmessage', params, { headers: { 'Authorization': 'p ' + this.gentokenSM() } });
	}

	reasignarCaso(params: any) {
		return this.http.post(this.globalipSM + '/api/socma/caso/reasignar', params, { headers: { 'Authorization': 'p ' + this.gentokenSM() } });
	}

	getCasoAbiertoCliente(params: any) {
		return this.http.post(this.globalipSM + '/api/socma/caso/getcasoabiertocliente', params, { headers: { 'Authorization': 'p ' + this.gentokenSM() } });
	}

	crearCasoPlantilla(params: any) {
		return this.http.post(this.globalipSM + '/api/socma/plantilla/crearcaso', params, { headers: { 'Authorization': 'p ' + this.gentokenSM() } });
	}

	crearCasoPlantillaCRM(params: any) {
		return this.http.post(this.globalipSMC + '/api/socma/plantilla/crearcasoCRM', params, { headers: { 'Authorization': 'p ' + this.gentokenSM() } });
	}

	cerrarCaso(params: any) {
		return this.http.post(this.globalipSM + '/api/socma/caso/cerrar', params, { headers: { 'Authorization': 'p ' + this.gentokenSM() } });
	}

	getCrmPlantilla(params: any) {
		return this.http.post(this.globalipSM + '/api/socma/template/getall', params, { headers: { 'Authorization': 'p ' + this.gentokenSM() } });
	}

	sendCrmPlantilla(params: any) {
		return this.http.post(this.globalipSM + '/api/socma/template/send', params, { headers: { 'Authorization': 'p ' + this.gentokenSM() } });
	}

	getfichaclientebytel(params: any) {
		return this.http.post(this.globalipSM + '/api/socma/cliente/getByPhone', params, { headers: { 'Authorization': 'p ' + this.gentokenSM() } });
	}

	getFichaByPhoneCreate(params: any) {
		return this.http.post(this.globalipSM + '/api/socma/cliente/getByPhoneCreate', params, { headers: { 'Authorization': 'p ' + this.gentokenSM() } });
	}

	tipificarLlamadaWex(params: any) {
		return this.http.post(this.globalipSM + '/api/socma/tipificarllamada', params, { headers: { 'Authorization': 'p ' + this.gentokenSM() } });
	}

	crearCasollamadaWex(params: any) {
		return this.http.post(this.globalipSM + '/api/socma/crearcasollamada', params, { headers: { 'Authorization': 'p ' + this.gentokenSM() } });
	}

	certificarTelefonia(params: any) {
		return this.http.post(this.globalipClientIsoft + '/api/isoft/certificacionTelefonia', params);
	}

	certificarTV(params: any) {
		return this.http.post(this.globalipClientIsoft + '/api/isoft/certificacionTv', params);
	}

	certificarHFC(params: any) {
		return this.http.post(this.globalipClientIsoft + '/api/isoft/diagnosticoHfc', params);
	}

	certificarFTTH(params: any) {
		return this.http.post(this.globalipClientIsoft + '/api/isoft/diagnosticoFtth', params);
	}
}

const myMap = new Map();

myMap.set('getconfig', '/api/socma/agente/getconfig');
myMap.set('avilogin', '/api/AVI/LOGIN');
myMap.set('getdatetime', '/api/PGV/system/getdatetime');

myMap.set('prevlista', '/api/PGV/CAMPANA/GET_PREV_LISTA');
myMap.set('deleteclientelista', '/api/PGV/CAMPANA/DELETE_CLIENTE_LISTA');

myMap.set('agentecampanalite', '/api/PGV/CAMPANA/GET_AGENTES');
myMap.set('cargacatalogo', '/api/PGV/GESTION/CARGAR_CATALOGO');

myMap.set('campanacatalogo', '/api/PGV/AGENTE/PAGINEO_CAMPANA_CATALOGO');
myMap.set('tablecampanacatalogo', '/api/PGV/AGENTE/PAGINEO_CATALOGO_AGENTE');
myMap.set('tablecampanacatalogocount', '/api/PGV/AGENTE/PAGINEO_CATALOGO_AGENTE_COUNT');

myMap.set('supervisorcampana', '/api/PGV/SUPERVISOR/CAMPANAS');
myMap.set('agentecampana', '/api/PGV/SUPERVISOR/AGENTES');
myMap.set('horascampana', '/api/PGV/SUPERVISOR/HORACAMPANA');
myMap.set('diacampana', '/api/PGV/SUPERVISOR/DIACAMPANA');
myMap.set('excelrellamadas', '/api/PGV/SUPERVISOR/EXCEL_RELLAMADA_CAMPANA');
myMap.set('rellamadas', '/api/PGV/SUPERVISOR/RELLAMADA_CAMPANA');
myMap.set('rellamadascount', '/api/PGV/SUPERVISOR/RELLAMADA_CAMPANA_COUNT');
myMap.set('pivotsupervisor', '/api/PGV/SUPERVISOR/DASHBOARD_PIVOT');
myMap.set('monitordeagentes', '/api/PGV/SUPERVISOR/MONITOR_AGENTE');

myMap.set('guardarTipificacionInBound', '/api/PGV/GESTION/GUARDAR_TIPIFICACION_INBOUND');
myMap.set('getviviendas', '/api/PGV/GESTION/getviviendas');
myMap.set('gettipocliente', '/api/PGV/GESTION/gettipocliente');
myMap.set('agregarygestionarcliente', '/api/PGV/GESTION/ADDCLIENTE');
myMap.set('getProductos', '/api/PGV/GESTION/getProductos');

myMap.set('campanasactivas', '/api/PGV/CAMPANA/CAMPANAS_ACTIVAS');
myMap.set('obtener_cliente', '/api/PGV/GESTION/OBTENER_CLIENTE');
myMap.set('obtener_cliente_rellamada', '/api/PGV/GESTION/OBTENER_CLIENTE_RELLAMADA');
myMap.set('obtener_cliente_volverAllamar', '/api/PGV/GESTION/OBTENER_CLIENTE_VOLVERALLAMAR');
myMap.set('obtener_cliente_ficha', '/api/PGV/GESTION/OBTENER_CLIENTE_FICHA');
myMap.set('finalizar_llamada', '/api/PGV/GESTION/FINALIZA_LLAMADA');
myMap.set('cambio_estado', '/api/PGV/GESTION/CAMBIAR_ESTADO');
myMap.set('get_tipologias', '/api/PGV/TIPOLOGIA/TIPOLOGIAS');
myMap.set('get_subtipologias', '/api/PGV/TIPOLOGIA/SUBTIPOLOGIAS');
myMap.set('verificaragendamiento', '/api/PGV/GESTION/VERIFICAR_AGENDAMIENTO');
myMap.set('reagendarytipificar', '/api/PGV/GESTION/REAGENDARYTIPIFICAR');
myMap.set('creartipologia', '/api/PGV/GESTION/CREAR_GESTION');
myMap.set('actualizarEstadoContacto', '/api/v1/fib/contactos/actualizar-estado-contacto');
myMap.set('historicocampana', '/api/PGV/GESTION/HISTORICO_CAMPANA');
myMap.set('obtenerbitacora', '/api/PGV/GESTION/OBTENER_BITACORA');
myMap.set('guardarcomentariobitacora', '/api/PGV/GESTION/GUARDAR_COMENTARIO_BITACORA');
myMap.set('historicotipologias', '/api/PGV/GESTION/OBTENER_GESTIONES');
myMap.set('estadisticas_dia', '/api/PGV/GESTION/ESTADISTICA_AGENTE_DIA');
myMap.set('estadisticas_mes', '/api/PGV/GESTION/ESTADISTICA_AGENTE_MES');
myMap.set('enviarcorreos', '/api/PGV/GESTION/ENVIARCORREO');

myMap.set('createciclo', '/api/PGV/CICLOVIDA/CREAR');
myMap.set('obtenerciclo', '/api/PGV/CICLOVIDA/OBTENER');
myMap.set('cleanciclo', '/api/PGV/CICLOVIDA/LIMPIAR');
myMap.set('cleanalarma', '/api/PGV/ALARMA/BORRAR');
myMap.set('createalarma', '/api/PGV/ALARMA/CREAR');
myMap.set('obteneralarma', '/api/PGV/ALARMA/OBTENER');
myMap.set('obtenerhorario', '/api/PGV/HORARIO/OBTENER');
myMap.set('createschedule', '/api/PGV/HORARIO/CREAR');
myMap.set('createcampana', '/api/PGV/CAMPANA/CREAR');
myMap.set('setautorizacioncamp', '/api/PGV/CAMPANA/SETAUTORIZACION');
myMap.set('getcampana', '/api/PGV/CAMPANA/PAGINEO');
myMap.set('getcampanacount', '/api/PGV/CAMPANA/CONTEO');
myMap.set('modifyCampana', '/api/PGV/CAMPANA/MODIFICAR');

myMap.set('createcampanaadmin', '/api/PGV/CAMPANA/ADMIN/CREAR');
myMap.set('getcampanaadmin', '/api/PGV/CAMPANA/ADMIN/PAGINEO');
myMap.set('getcampanaadmincount', '/api/PGV/CAMPANA/ADMIN/CONTEO');
myMap.set('crearcampanaxcontrata', '/api/PGV/CAMPANA/ADMIN/REPLICAR');
myMap.set('editarcampanaxcontrata', '/api/PGV/CAMPANA/ADMIN/EDITAR/REPLICAR');
myMap.set('editarfiltrodistclientes', '/api/PGV/CAMPANA/ADMIN/EDITAR/FILTRODISTCLI');
myMap.set('agenciascampana', '/api/PGV/CAMPANA/ADMIN/AGENCIAS_CAMPANA');
myMap.set('provinciascampana', '/api/PGV/CAMPANA/ADMIN/PROVINCIAS_CAMPANA');

myMap.set('getcampanasemaforo', '/api/PGV/CAMPANA/SEMAFORO');
myMap.set('gettotalescampanasadmin', '/api/PGV/CAMPANA/ADMIN/TOTALES');
myMap.set('getcampanasemaforoclientes', '/api/PGV/CAMPANA/SEMAFORO/CLIENTES');
myMap.set('getcampanasemaforoclientesporcampana', '/api/PGV/CAMPANA/SEMAFORO/CLIENTES/CAMPANA');
myMap.set('createeje', '/api/PGV/EJE/CREAR');
myMap.set('geteje', '/api/PGV/EJE/PAGINEO');
myMap.set('getejecount', '/api/PGV/EJE/CONTEO');
myMap.set('editeje', '/api/PGV/EJE/MODIFICAR');
myMap.set('deleteeje', '/api/PGV/EJE/BORRAR');
myMap.set('getrol', '/api/PGV/ROL/PAGINEO');
myMap.set('getrolcount', '/api/PGV/ROL/CONTEO');
myMap.set('createrol', '/api/PGV/ROL/CREAR');
myMap.set('editrol', '/api/PGV/ROL/MODIFICAR');
myMap.set('deleterol', '/api/PGV/ROL/BORRAR');

myMap.set('getpxa', '/api/PGV/PXA/PAGINEO');
myMap.set('getpxacount', '/api/PGV/PXA/CONTEO');
myMap.set('createpxa', '/api/PGV/PXA/CREAR');
myMap.set('editpxa', '/api/PGV/PXA/MODIFICAR');
myMap.set('deletepxa', '/api/PGV/PXA/BORRAR');
myMap.set('cargamasivapxa', '/api/PGV/PXA/CARGA_MASIVA');
myMap.set('simularpxa', '/api/PGV/PXA/SIMULAR');
myMap.set('auditoriapxa', '/api/PGV/PXA/AUDITORIA');

myMap.set('gettipologia', '/api/PGV/TIPOLOGIA/PAGINEO');
myMap.set('gettipologiacount', '/api/PGV/TIPOLOGIA/CONTEO');
myMap.set('createtipologia', '/api/PGV/TIPOLOGIA/CREAR');
myMap.set('edittipologia', '/api/PGV/TIPOLOGIA/MODIFICAR');
myMap.set('deletetipologia', '/api/PGV/TIPOLOGIA/BORRAR');
myMap.set('getsubtipologias', '/api/PGV/TIPOLOGIA/SUBTIPOLOGIAS');
myMap.set('getagente', '/api/PGV/AGENTE/PAGINEO');
myMap.set('getagenteagente', '/api/PGV/AGENTE/PAGINEO_AGENTE');
myMap.set('getagentecount', '/api/PGV/AGENTE/CONTEO');

myMap.set('getadminagente', '/api/PGV/ADMIN/AGENTE/PAGINEO');
myMap.set('getadminagentecount', '/api/PGV/ADMIN/AGENTE/CONTEO');
myMap.set('getadminagentecontrata', '/api/PGV/ADMIN/AGENTE/CONTRATA');

myMap.set('getcitasprogramadas', '/api/PGV/CITAS/PROGRAMADAS/getpagineo');
myMap.set('getcitasprogramadasdescarga', '/api/PGV/CITAS/PROGRAMADAS/getpagineodescarga');
myMap.set('getcountcitasprogramadas', '/api/PGV/CITAS/PROGRAMADAS/getcount');
myMap.set('updatecitaprogramada', '/api/PGV/CITAS/PROGRAMADAS/update');
myMap.set('histcitaprogramada', '/api/PGV/CITAS/PROGRAMADAS/historial');
myMap.set('gettecnicosxcontrata', '/api/PGV/CITAS/PROGRAMADAS/gettecnicosxcontrata');
myMap.set('editarcuentacita', '/api/PGV/CITAS/PROGRAMADAS/editarcuenta');

myMap.set('getcitassuspendidas', '/api/PGV/CITAS/SUSPENDIDAS/getpagineo');
myMap.set('getcitassuspendidasdescarga', '/api/PGV/CITAS/SUSPENDIDAS/getpagineodescarga');
myMap.set('getcountcitassuspendidas', '/api/PGV/CITAS/SUSPENDIDAS/getcount');
myMap.set('gettecnicosxcontrata_suspendidas', '/api/PGV/CITAS/SUSPENDIDAS/gettecnicosxcontrata');

myMap.set('listaroles', '/api/PGV/ROL/LISTAR');
myMap.set('createagente', '/api/PGV/AGENTE/CREAR');
myMap.set('editagente', '/api/PGV/AGENTE/MODIFICAR');
myMap.set('deleteagente', '/api/PGV/AGENTE/BORRAR');
myMap.set('loginagente', '/api/AGENTE/LOGIN');
myMap.set('loginagentewithoutpassword', '/api/AGENTE/LOGINWITHOUTPASSWORD');
myMap.set('updateWhatsapp', '/api/AGENTE/getwhatsappqr');
myMap.set('setSincronizar', '/api/AGENTE/setSincronizar');
myMap.set('listacampanas', '/api/PGV/CAMPANA/LISTAR');
myMap.set('asignarcampana', '/api/PGV/AGENTE/ASIGNAR_CAMPANA');
myMap.set('campanasagente', '/api/PGV/AGENTE/CAMPANAS_AGENTE');
myMap.set('saveMsgWhatsapp', '/api/PGV/AGENTE/SAVEMSGWHATSAPP');
myMap.set('obtenerrecordatorios', '/api/PGV/RECORDATORIO/OBTENER');
myMap.set('modificarrecordatorio', '/api/PGV/RECORDATORIO/MODIFICAR');
myMap.set('listacolumnas', '/api/PGV/CRITERIO/LISTAR_COLUMNAS');
myMap.set('asignarcriterios', '/api/PGV/CRITERIO/ASIGNAR');
myMap.set('obtenercriterios', '/api/PGV/CRITERIO/OBTENER_CRITERIOS');
myMap.set('getprovincias', '/api/PGV/AGENTE/GETPROVINCIAS');
myMap.set('getdistritosxprovincia', '/api/PGV/AGENTE/GETDISTRITOSXPROVINCIA');
myMap.set('getcorregimientosxdistrito', '/api/PGV/AGENTE/GETCORREGIMIENTOSXDISTRITO');
myMap.set('getprovinciasxagente', '/api/PGV/AGENTE/GETPROVINCIASXAGENTE');
myMap.set('getAreasAgente', '/api/PGV/AGENTE/getAreasAgente');
myMap.set('getContrata', '/api/PGV/AGENTE/getContrata');

myMap.set('getcorregimientos', '/api/PGV/AGENTE/getcorregimientos');

// ENTREVISTAS
myMap.set('getentrevista', '/api/PGV/ENTREVISTA/PAGINEO');
myMap.set('getentrevistacount', '/api/PGV/ENTREVISTA/CONTEO');
myMap.set('createentrevista', '/api/PGV/ENTREVISTA/CREAR');
myMap.set('editentrevista', '/api/PGV/ENTREVISTA/MODIFICAR');
myMap.set('deleteentrevista', '/api/PGV/ENTREVISTA/BORRAR');
myMap.set('getsubentrevistas', '/api/PGV/ENTREVISTA/SUBENTREVISTAS');
myMap.set('get_entrevistas', '/api/PGV/ENTREVISTA/ENTREVISTAS');
myMap.set('get_subentrevistas', '/api/PGV/ENTREVISTA/SUBENTREVISTAS');

// TIPIFICACION RECOLECTOR
myMap.set('gettipologiare', '/api/PGV/TIPIFICACION/RECOLECTOR/PAGINEO');
myMap.set('gettipologiarecount', '/api/PGV/TIPIFICACION/RECOLECTOR/CONTEO');
myMap.set('createtipologiare', '/api/PGV/TIPIFICACION/RECOLECTOR/CREAR');
myMap.set('edittipologiare', '/api/PGV/TIPIFICACION/RECOLECTOR/MODIFICAR');
myMap.set('deletetipologiare', '/api/PGV/TIPIFICACION/RECOLECTOR/BORRAR');
myMap.set('getsubtipologiare', '/api/PGV/TIPIFICACION/RECOLECTOR/SUBTIPOLOGIAS');
myMap.set('get_tipologiasre', '/api/PGV/TIPIFICACION/RECOLECTOR/TIPOLOGIAS');
myMap.set('get_subtipologiasre', '/api/PGV/TIPIFICACION/RECOLECTOR/SUBTIPOLOGIAS');

// CLIENTE DESDE SCL
myMap.set('getdataclient', '/api/PGV/CLIENTE/GETDATACLIENT');
myMap.set('deleteTareaProgramada', '/api/PGV/CLIENTE/deleteTareaProgramada');
// DISTRIBUIDORES
myMap.set('getdistribuidorxcampanapadre', '/api/PGV/DISTRIBUIDOR/ASIGCAMPANAPADRE');
myMap.set('getdistribuidor', '/api/PGV/DISTRIBUIDOR/PAGINEO');
myMap.set('getdistribuidorcount', '/api/PGV/DISTRIBUIDOR/CONTEO');
myMap.set('createdistribuidor', '/api/PGV/DISTRIBUIDOR/CREAR');
myMap.set('editdistribuidor', '/api/PGV/DISTRIBUIDOR/MODIFICAR');
myMap.set('setestadodistribuidor', '/api/PGV/DISTRIBUIDOR/SETESTADO');

// ADMIN-SUPERVISOR
myMap.set('getadminsupervisor', '/api/PGV/ADMINSUPER/PAGINEO');
myMap.set('getadminsupervisorcount', '/api/PGV/ADMINSUPER/CONTEO');
myMap.set('creatadminsupervisor', '/api/PGV/ADMINSUPER/CREAR');
myMap.set('editadminsupervisor', '/api/PGV/ADMINSUPER/MODIFICAR');
myMap.set('setestadoadminsupervisor', '/api/PGV/ADMINSUPER/SETESTADO');

myMap.set('getcapacidad', '/api/PGV/DISTRIBUIDOR/GETCAPACITY');
myMap.set('getcapacidadxprovincia', '/api/PGV/DISTRIBUIDOR/GETCAPACITYXPROVINCIA');
myMap.set('getcapacidadxtipotecnico', '/api/PGV/DISTRIBUIDOR/GETCAPACITYXTIPOTECNICO');
myMap.set('modificarcapacidad', '/api/PGV/DISTRIBUIDOR/MODIFYCAPACITY');
myMap.set('modificarcapacidadxprovincia', '/api/PGV/DISTRIBUIDOR/MODIFYCAPACITYXPROVINCIA');
myMap.set('modificarcapacidadxtipotecnico', '/api/PGV/DISTRIBUIDOR/MODIFYCAPACITYXTIPOTECNICO');

myMap.set('gettecnicos', '/api/PGV/DISTRIBUIDOR/GETTECNICO');
myMap.set('getcitas', '/api/PGV/DISTRIBUIDOR/GETCITAS');
myMap.set('actualizarcitasporagente', '/api/PGV/DISTRIBUIDOR/ACTUALIZARCITAS');

// ADMINISTRADORES
myMap.set('getadministrador', '/api/PGV/ADMINISTRADOR/PAGINEO');
myMap.set('getadministradorcount', '/api/PGV/ADMINISTRADOR/CONTEO');
myMap.set('createadministrador', '/api/PGV/ADMINISTRADOR/CREAR');
myMap.set('editadministrador', '/api/PGV/ADMINISTRADOR/MODIFICAR');
myMap.set('setestadoadministrador', '/api/PGV/ADMINISTRADOR/SETESTADO');

// RECOLECTORES DE EQUIPOS
myMap.set('getrecolector', '/api/PGV/RECOLECTOR/PAGINEO');
myMap.set('getrecolectorcount', '/api/PGV/RECOLECTOR/CONTEO');
myMap.set('createrecolector', '/api/PGV/RECOLECTOR/CREAR');
myMap.set('editrecolector', '/api/PGV/RECOLECTOR/MODIFICAR');
myMap.set('setestadorecolector', '/api/PGV/RECOLECTOR/SETESTADO');

// AREA
myMap.set('getallarea', '/api/PGV/AREA/ALL');
myMap.set('getarea', '/api/PGV/AREA/PAGINEO');
myMap.set('getareacount', '/api/PGV/AREA/CONTEO');
myMap.set('createarea', '/api/PGV/AREA/CREAR');
myMap.set('editarea', '/api/PGV/AREA/MODIFICAR');
myMap.set('setestadoarea', '/api/PGV/AREA/SETESTADO');

// EDIFICIO
myMap.set('getalledificio', '/api/PGV/EDIFICIO/ALL');
myMap.set('getedificio', '/api/PGV/EDIFICIO/PAGINEO');
myMap.set('getedificiocount', '/api/PGV/EDIFICIO/CONTEO');
myMap.set('createedificio', '/api/PGV/EDIFICIO/CREAR');
myMap.set('editedificio', '/api/PGV/EDIFICIO/MODIFICAR');
myMap.set('setestadoedificio', '/api/PGV/EDIFICIO/SETESTADO');
myMap.set('gethorariosedif', '/api/PGV/EDIFICIO/HORARIOS');
myMap.set('deletehorarioedif', '/api/PGV/EDIFICIO/DELETEHORARIO');
myMap.set('updatehorarioedif', '/api/PGV/EDIFICIO/UPDATEHORARIO');

// MENSAJERIAS
myMap.set('getlistacampana', '/api/PGV/mensajeria/campana/lista');
myMap.set('getcriteriocatalogo', '/api/PGV/mensajeria/catalogo');
myMap.set('getcriteriolista', '/api/PGV/mensajeria/catalogo/lista');
myMap.set('getmensajerias', '/api/PGV/mensajeria');
myMap.set('getmensajeriadetalle', '/api/PGV/mensajeria/detalle');
myMap.set('upsertmensajeria', '/api/PGV/mensajeria/save');
myMap.set('deletemensajeria', '/api/PGV/mensajeria/delete');
myMap.set('getarchivomensajeria', '/api/PGV/mensajeria/getArchivo');

// FILTRAR CLIENTE CAMPANA
myMap.set('filtrarclicamp', '/api/PGV/CAMPANA/FILTRAR_CLIENTES_CAMPANA');
myMap.set('pagineoclienteinvalido', '/api/PGV/CAMPANA/PAGINEO_CLIREPCAMP');
myMap.set('lotepagineoclienteinvalido', '/api/PGV/CAMPANA/LOTE_PAGINEO_CLIREPCAMP');
myMap.set('conteoclienteinvalido', '/api/PGV/CAMPANA/CONTEO_CLIREPCAMP');
myMap.set('getcampanaaut', '/api/PGV/CAMPANA/PAGINEO_AUT');
myMap.set('getcampanacountaut', '/api/PGV/CAMPANA/CONTEO_AUT');
myMap.set('upsertwhatsapp', '/api/PGV/CAMPANA/UPSERTWHATSAPP');
myMap.set('getmsjwhatsapp', '/api/PGV/CAMPANA/GETMSJWHATSAPP');

// CANALES DIGITALES
myMap.set('gettipo', '/api/PGV/CAMPANA/GETTIPOLOGIAS');
myMap.set('getsubtipo', '/api/PGV/CAMPANA/GETSUBTIPOLOGIAS');
myMap.set('insertcanalesdig', '/api/PGV/CAMPANA/INSERTCANALESDIG');
myMap.set('getcanalesdig', '/api/PGV/CAMPANA/GETCANALESDIG');
myMap.set('getreglaoport', '/api/PGV/CAMPANA/GETREGLAOPORT');
myMap.set('insertgestoroportunidad', '/api/PGV/CAMPANA/INSERT_REGLAOPORT');
myMap.set('getgestoroportunidad', '/api/PGV/CAMPANA/GET_REGLAOPORT');

myMap.set('getrazon', '/api/PGV/RAZONALARMA/PAGINEO');
myMap.set('getrazoncount', '/api/PGV/RAZONALARMA/CONTEO');
myMap.set('createrazon', '/api/PGV/RAZONALARMA/CREAR');
myMap.set('editrazon', '/api/PGV/RAZONALARMA/MODIFICAR');
myMap.set('deleterazon', '/api/PGV/RAZONALARMA/BORRAR');
myMap.set('listarazon', '/api/PGV/RAZONALARMA/LISTAR');

myMap.set('desasignarlista', '/api/PGV/CAMPANA/DESASIGNAR_LISTA');
myMap.set('deletelistacampana', '/api/PGV/CAMPANA/BORRAR_LISTA');
myMap.set('listaexistente', '/api/PGV/CAMPANA/LISTA_EXISTENTE');
myMap.set('exportarlista', '/api/PGV/CAMPANA/EXPORTAR_LISTA');
myMap.set('exportarlistalote', '/api/PGV/CAMPANA/EXPORTAR_LISTA_LOTE');
myMap.set('exportarlistaadminlote', '/api/PGV/CAMPANA/ADMIN/EXPORTAR_LISTA_LOTE');
myMap.set('exportarlistacount', '/api/PGV/CAMPANA/EXPORTAR_LISTA_COUNT');
myMap.set('exportarlistaadmincount', '/api/PGV/CAMPANA/ADMIN/EXPORTAR_LISTA_COUNT');
myMap.set('deletecampana', '/api/PGV/CAMPANA/BORRAR');
myMap.set('asignaragente', '/api/PGV/CAMPANA/ASIGNAR_AGENTE');
myMap.set('getencabezados', '/api/PGV/CAMPANA/GETENCABEZADOS');
myMap.set('duplicarcampana', '/api/PGV/CAMPANA/DUPLICAR');
myMap.set('getagencias', '/api/PGV/CAMPANA/GETAGENCIAS');
myMap.set('distribuirclientes', '/api/PGV/CAMPANA/DISTRIBUIRCLIENTES');

myMap.set('modificarregla', '/api/PGV/REGLA/MODIFICAR');
myMap.set('obtenerregla', '/api/PGV/REGLA/OBTENER');
myMap.set('obtenerareadeagente', '/api/PGV/REGLA/GET_AREA_DE_AGENTE');
myMap.set('obtenerareadedistribuidor', '/api/PGV/REGLA/GET_AREA_DE_DISTRIBUIDOR');

myMap.set('getgestionespv', '/api/PGV/GESTION/PAGINEO_GESTIONES_POSTVENTA');
myMap.set('getgestionespvcount', '/api/PGV/GESTION/CONTEO_GESTIONES_POSTVENTA');
myMap.set('ejecutadoscount', '/api/PGV/GESTION/CONTEO_GESTIONES_POSTVENTA_EJECUTADOS');
myMap.set('noejecutadoscount', '/api/PGV/GESTION/CONTEO_GESTIONES_POSTVENTA_NOEJECUTADOS');
myMap.set('actualizargestion', '/api/PGV/GESTION/ACTUALIZAR_GESTION_POSTVENTA');
myMap.set('reportepostventa', '/api/PGV/GESTION/OBTENER_REPORTE_POSTVENTA');
myMap.set('descargagestion', '/api/PGV/GESTION/DESCARGA_GESTIONES_POSTVENTA');

myMap.set('histllamadasagente', '/api/PGV/AGENTE/HIST_LLAMADA_AGENTE');
myMap.set('histllamadasagentecount', '/api/PGV/AGENTE/HIST_LLAMADA_AGENTE_COUNT');

myMap.set('rellamadasagente', '/api/PGV/AGENTE/RELLAMADA_AGENTE');
myMap.set('rellamadasagentecount', '/api/PGV/AGENTE/RELLAMADA_AGENTE_COUNT');

myMap.set('getcampanapvbo', '/api/PGV/CAMPANA/PAGINEOPOSTVENTABO');

myMap.set('validatraslapecampana', '/api/PGV/CAMPANA/VALIDA_TRASLAPE_CAMPANA');
myMap.set('validatraslapehorariocampana', '/api/PGV/CAMPANA/VALIDA_TRASLAPE_HORARIO_CAMPANA');
myMap.set('camposextracep', '/api/PGV/TIPOLOGIA/CAMPOS_EXTRAS_CEP');

myMap.set('gettokenbi', '/api/PGV/REPORT/GET_TOKEN_BI');
myMap.set('cambiapass', '/api/PGV/AGENTE/CAMBIA_PASS');
myMap.set('cargamasivaagentes', '/api/PGV/AGENTE/CARGA_MASIVA');
myMap.set('eliminamasivaagentes', '/api/PGV/AGENTE/ELIMINA_MASIVA');
myMap.set('asignamasivaagentes', '/api/PGV/AGENTE/ASIGNA_CAMPANA_MASIVA');
myMap.set('habilitaragente', '/api/PGV/AGENTE/HABILITAR');
myMap.set('historicoagente', '/api/PGV/AGENTE/HISTORICO_AGENTE');
myMap.set('editagentehistorico', '/api/PGV/AGENTE/HISTORICO_AGENTE_EDIT');
myMap.set('historicoagenterol', '/api/PGV/AGENTE/HISTORICO_AGENTE_ROL');
myMap.set('gethorariosagente', '/api/PGV/AGENTE/HORARIO_AGENTE');

// Multiusos: Tecnicos compartidos entre distribuidores
myMap.set('toggleDistribuidorTecnico', '/api/PGV/AGENTE/TOGGLE_DISTRIBUIDOR');
myMap.set('getDistribuidoresTecnico', '/api/PGV/AGENTE/GET_DISTRIBUIDORES_TECNICO');

myMap.set('exportaradmindashcount', '/api/PGV/REPORT/EXPORTAR_ADMIN_DASH_COUNT');
myMap.set('exportaradmindashlote', '/api/PGV/REPORT/EXPORTAR_ADMIN_DASH_LOTE');

myMap.set('getChatsSupervisor', '/api/socma/chats/supervisor');
myMap.set('getcliente', '/api/socma/chats/getcliente');
myMap.set('upsertfichacliente', '/api/socma/chats/upsertfichacliente');

myMap.set('saveConfigBlackList', '/api/PGV/BLACKLIST/saveConfigBlackList');
myMap.set('getConfigBlackList', '/api/PGV/BLACKLIST/getConfigBlackList');
myMap.set('sendSMSorWA', '/api/PGV/BLACKLIST/sendSMSorWA');

myMap.set('getAllEnviosMasivos', '/api/PGV/BLACKLIST/getAllEnviosMasivos');
myMap.set('getEnviosMasivos', '/api/PGV/BLACKLIST/getEnviosMasivos');
myMap.set('getEnviosMasivosCount', '/api/PGV/BLACKLIST/getEnviosMasivosCount');
myMap.set('upsertEnvioMasivo', '/api/PGV/BLACKLIST/upsertEnvioMasivo');
myMap.set('sendEnvioMasivo', '/api/PGV/BLACKLIST/sendEnvioMasivo');
myMap.set('descargarClientes', '/api/PGV/BLACKLIST/descargarClientes');
myMap.set('getTokenConsentimiento', '/api/PGV/BLACKLIST/getTokenConsentimiento');

//  ----------- MARTECH --------------------
myMap.set('insert_datos_idcaso_wex', '/ws/martech/insertidcasowex');
myMap.set('buscarcliente', '/api/PGV/CLIENTE/BUSCARCLIENTE');
myMap.set('insert_datos_gestiones', '/ws/martech/insertgestiones');
myMap.set('getservicioscli', '/api/PGV/CLIENTE/GETSERVICIOS');
myMap.set('getproductoscli', '/api/PGV/CLIENTE/GETPRODUCTOS');
myMap.set('gettoken', '/api/PGV/CLIENTE/GETTOKEN');
myMap.set('actualizarcliente', '/api/PGV/cliente/update');

myMap.set('setestadoequipore', '/api/PGV/equiporecolector/setestado');
myMap.set('getequipore', '/api/PGV/equiporecolector/pagineo');
myMap.set('getcontequipore', '/api/PGV/equiporecolector/conteo');

myMap.set('getCasosWP', '/api/PGV/seguimiento/getCasosWP');
myMap.set('insertCasosWP', '/api/PGV/seguimiento/insertCasosWP');

myMap.set('getprioridades', '/api/PGV/PRIORIDAD/OBTENER');
myMap.set('sendplantillascampana', '/api/reparaciones/sendplantillas');
myMap.set('sendPlantillaCliente', '/api/reparaciones/sendPlantillaCliente');

//  ----------- AGENDAR POR ZONA DE TECNICO --------------------
myMap.set('getPosiblesFechasdeCitaxZona', '/api/agendarcita/getPosiblesFechasdeCitaxZona');
myMap.set('obtenerCitasZonasEliminadas', '/api/agendarcita/obtenerCitasZonasEliminadas');
myMap.set('obtenerCantCitasxZonasEliminadas', '/api/agendarcita/obtenerCantCitasxZonasEliminadas');
myMap.set('getreportezona', '/api/agendarcita/getreportezona');
myMap.set('getcontreportezona', '/api/agendarcita/getcontreportezona');

//  ----------- CONFIG ZONA --------------------
myMap.set('getconfigprovincia', '/api/config/getconfigprovincia');
myMap.set('getconfigdistritoxprovincia', '/api/config/getconfigdistritoxprovincia');
myMap.set('getconfigcorregimientoxdistrito', '/api/config/getconfigcorregimientoxdistrito');
myMap.set('getconfigtecnicoxzona', '/api/config/getconfigtecnicoxzona');
myMap.set('updateconfigzonas', '/api/config/updateconfigzonas');

//  ----------- REPORTES --------------------
myMap.set('getreportezona_provincias', '/api/reporte/disp/getprovincia');
myMap.set('getreportezona_distrito', '/api/reporte/disp/getdistritoxprovincia');
myMap.set('getreportezona_corregimiento', '/api/reporte/disp/getcorregimientoxdistrito');
myMap.set('getreportezona_tecnico', '/api/reporte/disp/gettecnicoxcorregimiento');
myMap.set('getbitacoracliente', '/api/reporte/disp/getbitacoracliente');
myMap.set('getClientesqueCancelaronCita', '/api/reporte/disp/getClientesqueCancelaronCita');
myMap.set('getContClientesqueCancelaronCita', '/api/reporte/disp/getContClientesqueCancelaronCita');
myMap.set('verificarAntesDeVolverALlamar', '/api/reporte/disp/verificarAntesDeVolverALlamar');
myMap.set('getClientesqueCancelaronCitaxContrata', '/api/reporte/disp/getClientesqueCancelaronCitaxContrata');
myMap.set('getContClientesqueCancelaronCitaxContrata', '/api/reporte/disp/getContClientesqueCancelaronCitaxContrata');

myMap.set('getClientesFallaPersiste', '/api/reporte/disp/getClientesFallaPersiste');
myMap.set('getContClientesFallaPersiste', '/api/reporte/disp/getContClientesFallaPersiste');
myMap.set('getClientesFallaPersistexContrata', '/api/reporte/disp/getClientesFallaPersistexContrata');

myMap.set('listarContacto', '/api/PGV/CONTACTO/LISTAR');
myMap.set('listarContactoCount', '/api/PGV/CONTACTO/CONTEO');
myMap.set('listarBitacoraProactivos', '/api/PGV/CONTACTO/BITACORA/LISTAR');
myMap.set('contarBitacoraProactivos', '/api/PGV/CONTACTO/BITACORA/CONTEO');
myMap.set('getContClientesFallaPersistexContrata', '/api/reporte/disp/getContClientesFallaPersistexContrata');

//  ----------- ENCUESTA --------------------
myMap.set('sendPlantillasEncuesta', '/api/encuesta/sendPlantillasEncuesta');
myMap.set('evaluarFallasEnvEncuesta', '/api/encuesta/evaluarFallasEnvEncuesta');
myMap.set('getContPlantillasEncuestaaEnviar', '/api/encuesta/getContPlantillasEncuestaaEnviar');
myMap.set('confirmacionEnvioEncuesta', '/api/encuesta/confirmacionEnvio');

myMap.set('getconfig2xarea', '/api/PGV/DISTRIBUIDOR/getconfig2xarea');
myMap.set('modifyconfig2xarea', '/api/PGV/DISTRIBUIDOR/modifyconfig2xarea');
myMap.set('getconfig2xareaHist', '/api/PGV/DISTRIBUIDOR/getconfig2xareaHist');
myMap.set('getalarmasxarea', '/api/PGV/ALARMAS/getalarmasxarea');
myMap.set('getubicacioncitas', '/api/PGV/REPORT/getubicacioncitas');
myMap.set('updateUbicacionCita', '/api/PGV/REPORT/updateUbicacionCita');

myMap.set('getCantRegistros', '/api/PGV/REPORT/getCantRegistros');
myMap.set('getRegistros', '/api/PGV/REPORT/getRegistros');

//  ---------- REPORTES --------------------
myMap.set('getreporte1', '/api/PGV/monitoreo/getreporte1');
myMap.set('getreporte2', '/api/PGV/monitoreo/getreporte2');
myMap.set('getReporteDash', '/api/PGV/monitoreo/getReporteDash');
myMap.set('getDetalleHora', '/api/PGV/monitoreo/getDetalleHora');
myMap.set('getDetalleSubtipologia', '/api/PGV/monitoreo/getDetalleSubtipologia');
myMap.set('getMonitoreoDescargaCount', '/api/PGV/monitoreo/getDescargaCount');
myMap.set('getMonitoreoDescargaData', '/api/PGV/monitoreo/getDescargaData');

myMap.set('getPendientes', '/api/PGV/alarmastp/pendientes');
myMap.set('getHistAlarmasxAgente', '/api/PGV/alarmastp/getHistAlarmasxAgente');
myMap.set('tomarAlarma', '/api/PGV/alarmastp/tomar');
myMap.set('resolverAlarma', '/api/PGV/alarmastp/resolver');
myMap.set('reintentoResolverAlarma', '/api/PGV/alarmastp/reintentoresolver');
myMap.set('gestionarclienteahora', '/api/PGV/alarmastp/gestionarclienteahora');
myMap.set('getReporteAlarmas', '/api/PGV/alarmastp/getReporteAlarmas');
myMap.set('getContReporteAlarmas', '/api/PGV/alarmastp/getContReporteAlarmas');
myMap.set('descargarReporteAlarmas', '/api/PGV/alarmastp/descargarReporteAlarmas');

//  ---------- CRUCE SGO --------------------
myMap.set('getCruceSGO', '/api/PGV/crucesgo/getCruceSGO');
myMap.set('getContCruceSGO', '/api/PGV/crucesgo/getContCruceSGO');
myMap.set('descargarCruceSGO', '/api/PGV/crucesgo/descargarCruceSGO');

//  ---------- CERTIFICADO NACIMIENTO --------------------
myMap.set('getHistorialCertificado', '/api/PGV/CERTIFICACION_TELEFONIA/PAGINEO');
myMap.set('getContHistorialCertificado', '/api/PGV/CERTIFICACION_TELEFONIA/CONTEO');
myMap.set('getUsuariosCertificado', '/api/PGV/CERTIFICACION_TELEFONIA/USUARIOS');
myMap.set('descargarHistorialCertificado', '/api/PGV/CERTIFICACION_TELEFONIA/DESCARGA');